import { ControlsApi } from 'Api/Controls/ControlsApi';
import { CircleIndicatorControlEffectiveness } from 'Components/CircleIndicators/CircleIndicatorControlEffectiveness';
import { ComplianceRequirementStatusIconAndStatusText } from 'Components/ComplianceRequirementStatus/ComplianceRequirementStatus';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { CONTROL_ASSESSMENT_HISTORICAL } from 'Config/PathTemplates';
import { makeComplianceRequirementHumanReadable } from 'Helpers/ComplianceRequirementFormatter/ComplianceRequirementFormatter';
import { getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getFrameworkGroupControlURL } from 'Helpers/URLBuilder/URLBuilder';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { AssessmentHistory, Comment, OperationalControl } from 'Models/OperationalControls';
import { ArtificialIntelligenceControlAssessmentResults } from 'Pages/ControlDetails/AssessmentDetails/ConductAssessment/ArtificialIntelligenceControlAssessment/ArtificialIntelligenceControlAssessmentResults';
import { ControlText } from 'Pages/ControlDetails/ControlText/ControlText';
import detailsPageStyles from 'Styles/DetailsPage.module.css';

import styles from './HistoricalControlAssessment.module.css';

export interface HistoricalControlAssessmentProps {
    controlsApi: ControlsApi;
}

/**
 * Renders two page sections for a historical control assessment:
 * * Assessment details (effectiveness, comments, requirements, etc.)
 * * AI assessment, if present
 */
export const HistoricalControlAssessment = (props: HistoricalControlAssessmentProps) => {
    return (
        <PageDataLoader
            path={CONTROL_ASSESSMENT_HISTORICAL}
            fetchData={async (urlParams) => {
                const [controlResponse, assessmentHistoryResponse] = await Promise.all([props.controlsApi.getControlDetails(urlParams.controlFramework, urlParams.controlGroupId, urlParams.controlId), props.controlsApi.getAssessmentHistoryByTimestamp(urlParams.controlFramework, urlParams.controlGroupId, urlParams.controlId, urlParams.timestamp)]);
                return { assessmentHistory: assessmentHistoryResponse.data, control: controlResponse.data };
            }}
            renderForm={(data, urlParams) => <HistoricalControlAssessmentPage assessmentHistory={data.assessmentHistory} control={data.control} controlFramework={urlParams.controlFramework} controlGroupId={urlParams.controlGroupId} controlId={urlParams.controlId} />}
        />
    );
};

interface HistoricalControlAssessmentPageProps {
    assessmentHistory: AssessmentHistory;
    control: OperationalControl;
    controlFramework: string;
    controlGroupId: string;
    controlId: string;
}

const HistoricalControlAssessmentPage = (props: HistoricalControlAssessmentPageProps) => {
    return (
        <PageLayoutDefault
            headerBreadcrumb={
                <Breadcrumb textColor="blue">
                    <BreadcrumbLink link={getFrameworkGroupControlURL(props.controlFramework)}>{props.controlFramework}</BreadcrumbLink>
                    <BreadcrumbLink link={getFrameworkGroupControlURL(`${props.controlFramework}#${props.controlGroupId}`)}>{props.control.metadata.control_group_name}</BreadcrumbLink>
                    <BreadcrumbLink link={`${getFrameworkGroupControlURL(`${props.controlFramework}#${props.controlGroupId}#${props.controlId}`)}#assessment`}>{props.control.metadata.control_name ?? props.controlId}</BreadcrumbLink>
                    <BreadcrumbText>Assessment History</BreadcrumbText>
                </Breadcrumb>
            }
            headerTitle={getHumanReadableControlIdFromControl(props.control)}
            headerDescription={<ControlText color="blue" controlText={props.control.metadata.control_text} textVariant="Header3" />}
            body={[
                {
                    title: 'Control Assessment',
                    content: <AssessmentHistorySnapshot assessmentHistory={props.assessmentHistory} />,
                },
                ...(props.assessmentHistory.ai_control_assessment?.final_result
                    ? [
                          {
                              title: 'SummIT Guide™ Control Assessment',
                              content: <ArtificialIntelligenceControlAssessmentResults complianceRequirements={props.assessmentHistory.attached_requirements ?? []} control={props.control} finalResult={props.assessmentHistory.ai_control_assessment.final_result} />,
                          },
                      ]
                    : []),
            ]}
        />
    );
};

interface AssessmentHistorySnapshotProps {
    assessmentHistory: AssessmentHistory;
}

const AssessmentHistorySnapshot = (props: AssessmentHistorySnapshotProps) => {
    const { assessment, attached_requirements, timestamp } = props.assessmentHistory;

    const ownerComments = assessment.owner_comments ?? [];

    return (
        <div className={detailsPageStyles.detailsContainer}>
            <div className={detailsPageStyles.detailsGridSection}>
                <div className={detailsPageStyles.detailsGridColumn}>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Completed" />
                        <Text variant="Text2">{iso8601ToUsDateShort(timestamp)}</Text>
                    </div>
                </div>
                <div className={detailsPageStyles.detailsGridColumn}>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Due Date" />
                        <Text variant="Text2">{assessment.assessment_due_date ? iso8601ToUsDateShort(assessment.assessment_due_date) : '-'}</Text>
                    </div>
                </div>
                <div className={detailsPageStyles.detailsGridColumn}>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Effectiveness" />
                        <CircleIndicatorControlEffectiveness effectiveness={assessment.assessment_effectiveness} />
                    </div>
                </div>
            </div>
            <div className={detailsPageStyles.labelAndValueContainer}>
                <FieldLabel fieldName="Owner Comments" />
                {ownerComments.length === 0 ? (
                    <Text>-</Text>
                ) : (
                    <div className={styles.columnWithGap}>
                        {ownerComments.map((comment, index) => (
                            <CommentWithAttribution key={index} comment={comment} />
                        ))}
                    </div>
                )}
            </div>
            <div className={detailsPageStyles.labelAndValueContainer}>
                <FieldLabel fieldName="Reviewer Comment" />
                {assessment.reviewer_comment ? <CommentWithAttribution comment={assessment.reviewer_comment} /> : <Text noStyles>-</Text>}
            </div>
            <div className={detailsPageStyles.labelAndValueContainer}>
                <FieldLabel fieldName="Requirements" />
                {!attached_requirements || attached_requirements.length === 0 ? (
                    <Text noStyles>-</Text>
                ) : (
                    <div className={styles.columnWithGap}>
                        {attached_requirements.map((requirement) => (
                            <div key={`${requirement.regulation}#${requirement.identifier}`}>
                                <div className={styles.column}>
                                    <Text variant="Text2" noStyles>
                                        {makeComplianceRequirementHumanReadable(requirement.regulation, requirement.identifier)}
                                    </Text>
                                    {requirement.text && <Text noStyles>{requirement.text}</Text>}
                                </div>
                                <ComplianceRequirementStatusIconAndStatusText regulation={requirement.regulation} status={requirement.status} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

interface CommentWithAttributionProps {
    comment: Comment;
}

/** Renders a comment's text, followed by who wrote it and when (if known). */
const CommentWithAttribution = (props: CommentWithAttributionProps) => {
    const cachedData = useCachedData();

    const userName = props.comment.user_id ? getUserNameFromSubject(props.comment.user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) : undefined;
    const attribution = [userName, props.comment.timestamp ? iso8601ToUsDateShort(props.comment.timestamp) : undefined].filter(Boolean).join(' ');

    return (
        <div className={styles.column}>
            <Text preserveLineBreaks noStyles>
                {props.comment.text}
            </Text>
            {attribution && (
                <Text variant="Text4" color="darkGray" noStyles>
                    {attribution}
                </Text>
            )}
        </div>
    );
};
